/**
 * SessionExpiringDialog — Modal d'avertissement avant déconnexion automatique.
 *
 * Affiché 2 minutes avant la fin du timer d'inactivité. L'utilisateur peut :
 *   - Cliquer « Rester connecté » → réinitialise le timer
 *   - Cliquer « Se déconnecter »  → déconnexion immédiate
 *   - Ne rien faire → logout auto à expiration
 *
 * Mobile-first, accessible (Esc = rester connecté), z-index élevé pour
 * passer au-dessus de tout autre modal applicatif.
 */
import React, { useEffect } from 'react';
import { Clock, LogOut } from 'lucide-react';
import { Button } from './ui/button';

const fmtMmSs = (totalSec) => {
  const s = Math.max(0, totalSec | 0);
  const mm = String(Math.floor(s / 60)).padStart(1, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const SessionExpiringDialog = ({ open, secondsLeft, onStay, onLogout }) => {
  // Esc = rester connecté (geste rapide, le plus prudent)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onStay?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onStay]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expiring-title"
      data-testid="session-expiring-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Clock className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 id="session-expiring-title" className="text-lg font-semibold text-zinc-900">
              Votre session va expirer bientôt
            </h2>
            <p className="text-sm text-zinc-600 mt-1">
              Pour des raisons de sécurité, vous serez déconnecté automatiquement dans
              {' '}<strong className="text-amber-700">{fmtMmSs(secondsLeft)}</strong>.
            </p>
            <p className="text-xs text-zinc-500 mt-2">
              Cliquez sur <strong>« Rester connecté »</strong> pour continuer votre session.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
          <Button
            variant="outline"
            onClick={onLogout}
            className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid="session-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Se déconnecter
          </Button>
          <Button
            onClick={onStay}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="session-stay-btn"
            autoFocus
          >
            Rester connecté
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiringDialog;
